import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Plus, X, XCircle } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';
import { isSprAnswerCorrect, parseSprNumber } from '../../constants';

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
}

/**
 * Accepted answers for a grid-in question, plus a tester: type what a student
 * might enter and see whether the grader would accept it.
 */
export function SprAnswersEditor({ value, onChange }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [trial, setTrial] = useState('');
  const draftInvalid = draft.trim() !== '' && parseSprNumber(draft) === null;

  const add = () => {
    const v = draft.trim();
    if (!v || parseSprNumber(v) === null || value.includes(v)) return;
    onChange([...value, v]);
    setDraft('');
  };

  const verdict = trial.trim() ? isSprAnswerCorrect(trial, value) : null;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('exam.spr.accepted')}</p>
        <div className="flex flex-wrap gap-2">
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-1 font-mono text-sm">
              {v}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== v))}
                aria-label={t('exam.spr.remove', { value: v })}
                className="rounded-full p-0.5 hover:bg-background"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          {!value.length && <span className="text-sm text-muted-foreground">{t('exam.spr.none_yet')}</span>}
        </div>
        <div className="flex max-w-sm gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
            placeholder="2/3"
            aria-label={t('exam.spr.add_answer')}
            aria-invalid={draftInvalid}
            className="font-mono"
          />
          <Button type="button" variant="outline" onClick={add} disabled={!draft.trim() || draftInvalid}>
            <Plus className="mr-1 h-4 w-4" aria-hidden /> {t('exam.spr.add')}
          </Button>
        </div>
        <p className={cn('text-xs', draftInvalid ? 'text-destructive' : 'text-muted-foreground')}>
          {draftInvalid ? t('exam.spr.invalid') : t('exam.spr.author_help')}
        </p>
      </div>

      <div className="rounded-lg border border-dashed border-border p-3">
        <label className="block text-sm font-medium text-foreground" htmlFor="spr-trial">
          {t('exam.spr.tester')}
        </label>
        <div className="mt-2 flex items-center gap-3">
          <Input id="spr-trial" value={trial} onChange={(e) => setTrial(e.target.value)} placeholder=".6667" className="max-w-[10rem] font-mono" />
          {verdict !== null && (
            <span className={cn('inline-flex items-center gap-1.5 text-sm font-medium', verdict ? 'text-success-600' : 'text-destructive')} aria-live="polite">
              {verdict ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <XCircle className="h-4 w-4" aria-hidden />}
              {verdict ? t('exam.spr.would_accept') : t('exam.spr.would_reject')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default SprAnswersEditor;
